import { Field, Heading, Input, Label, ValidationMessage, Dialog } from '@innovationnorway/cl-react';
import React from 'react';
import { useCluster } from 'src/features/cluster';
import useToggle from 'src/hooks/use-toggle';
import { sponsorColumns } from '../constants/sponsor-columns';
import DataTableV3 from 'src/components/DataTableV3';
import SelectSponsorTable from '../components/SelectSponsorTable';
import AddEditButton from '../components/AddEditButton';
import { Controller } from 'react-hook-form';
import { SponsorListType, useSponsorList } from '../hooks/use-sponsor-list';
import useContactInfoForm, { ContactInfoData } from '../hooks/subforms/use-contact-info-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';

const ContactInfoPage = (props: SubFormProps<ContactInfoData>) => {
  const { t: tServices } = useTranslation('services');
  const { cluster } = useCluster();
  const { getSponsorList } = useSponsorList();
  const { errors } = props;
  const { form, registerWithRules, rules } = useContactInfoForm(props);

  const { control, setValue, getValues } = form;

  const [selectedSponsors, setSelectedSponsors] = React.useState<SponsorListType[]>(() =>
    getSponsorList(getValues('sponsors')),
  );
  const { isActive, activate, deactivate } = useToggle(false);

  return (
    <div>
      <Heading level={1}>{tServices('contact.title')}</Heading>
      <div className="display--flex flex-direction--column gap--2 margin-top--2">
        <Field className="width--100">
          <Label>{tServices('contact.name.label')}</Label>
          <Field.Description data-size="sm">{tServices('contact.name.help')}</Field.Description>
          <Input
            defaultValue={`${cluster?.mainContact?.firstName} ${cluster?.mainContact?.lastName}`}
            {...registerWithRules('contactName')}
          />
          {errors.contactName?.message && <ValidationMessage>{errors.contactName.message}</ValidationMessage>}
        </Field>
        <Field>
          <Label>{tServices('contact.email.label')}</Label>
          <Input {...registerWithRules('contactEmail')} maxLength={500} />
          {errors.contactEmail?.message && (
            <ValidationMessage>{errors.contactEmail.message}</ValidationMessage>
          )}
        </Field>
        <Field>
          <Label>{tServices('contact.phone.label')}</Label>
          <Input {...registerWithRules('contactPhone')} />
          {errors.contactPhone?.message && (
            <ValidationMessage>{errors.contactPhone.message}</ValidationMessage>
          )}
        </Field>
      </div>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div>
          <Field>
            <Label>{tServices('contact.supplier.question')}</Label>
            <Field.Description data-size="sm">{tServices('contact.supplier.info')}</Field.Description>
            <p>{cluster?.name}</p>
          </Field>
        </div>
        <div className="display--flex width--100 flex-direction--column">
          <Label>{tServices('contact.client.question')}</Label>
          <Controller
            control={control}
            name="sponsors"
            rules={rules.sponsors}
            render={({ fieldState }) => (
              <Field className="width--100">
                <DataTableV3
                  disableSelection
                  disablePagination
                  disableColumnEditor
                  disableGlobalFilter
                  disableExport
                  hideFilterText
                  hideScrollbar
                  data={selectedSponsors}
                  columns={sponsorColumns}
                  id={'businessSupportOrgs'}
                  placeholderText={tServices('contact.client.placeholder')}
                />
                <AddEditButton
                  hasSelection={selectedSponsors.length > 0}
                  addLabel={tServices('form.add')}
                  editLabel={tServices('form.edit.button')}
                  onClick={() => activate()}
                  className="margin-top--2 padding--2"
                />
                {(fieldState.error || errors?.sponsors) && (
                  <ValidationMessage>
                    {fieldState.error?.message || errors?.sponsors?.message}
                  </ValidationMessage>
                )}
                <Dialog
                  open={isActive}
                  onClose={() => deactivate()}
                  title={tServices('contact.client.dialogTitle')}
                >
                  <SelectSponsorTable
                    selectedSponsors={selectedSponsors}
                    setSelectedSponsors={setSelectedSponsors}
                    setValue={setValue}
                    close={deactivate}
                  />
                </Dialog>
              </Field>
            )}
          ></Controller>
        </div>
      </div>
    </div>
  );
};

export default ContactInfoPage;
